<template>
  <Transition name="mobile-sidebar">
    <div
      v-if="open"
      class="mobile-sidebar-root mobile-sidebar-theme"
      :class="themeClass"
      :style="themeTokenStyle"
      role="dialog"
      aria-modal="true"
      :aria-label="resolved.menuAriaLabel"
    >
      <button
        type="button"
        class="mobile-sidebar-scrim"
        :aria-label="resolved.closeAriaLabel"
        @click="emitClose"
      />

      <aside class="mobile-sidebar">
        <div class="mobile-sidebar__panel">
          <header class="mobile-sidebar__head">
            <GcsaLogo
              class="mobile-sidebar__logo"
              :href="resolvedLogoHref"
              :title="resolvedLogoTitle"
              :tagline="resolvedLogoTagline"
              size="md"
              layout="row"
              :theme="theme || undefined"
              :show-wordmark="true"
              :show-tagline="showLogoTagline"
              @click="onLogoClick"
            />
            <button
              type="button"
              class="mobile-sidebar__close"
              :aria-label="resolved.closeAriaLabel"
              @click="emitClose"
            >
              <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
                <path
                  d="M1.4 1.4 10.6 10.6 M10.6 1.4 1.4 10.6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.15"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </header>

          <nav class="mobile-sidebar__nav" :aria-label="resolved.navAriaLabel">
            <ul class="mobile-sidebar__list">
              <li
                v-for="section in resolved.navItems"
                :key="section.key"
                class="mobile-sidebar__item"
              >
                <component
                  :is="hasSubmenu(section) ? 'button' : 'a'"
                  :href="hasSubmenu(section) ? undefined : section.href"
                  :type="hasSubmenu(section) ? 'button' : undefined"
                  class="mobile-sidebar__link"
                  :class="{ 'is-active': section.key === activeNavKey }"
                  :target="
                    !hasSubmenu(section) && section.external ? '_blank' : undefined
                  "
                  :rel="
                    !hasSubmenu(section) && section.external
                      ? 'noopener noreferrer'
                      : undefined
                  "
                  :aria-expanded="
                    hasSubmenu(section) ? expandedSection === section.key : undefined
                  "
                  @click="onTileClick($event, section)"
                >
                  <UiNavIcon :name="section.icon || 'home'" :svg="section.iconSvg" premium />
                  <span class="mobile-sidebar__copy">
                    <span class="mobile-sidebar__label">{{ section.label }}</span>
                  </span>
                  <span
                    v-if="hasSubmenu(section)"
                    class="mobile-sidebar__chevron"
                    :class="{ 'is-open': expandedSection === section.key }"
                    aria-hidden="true"
                  />
                </component>

                <ul
                  v-if="expandedSection === section.key && section.children?.length"
                  class="mobile-sidebar-sub"
                >
                  <li v-for="child in section.children" :key="child.key">
                    <a
                      :href="child.href"
                      class="mobile-sidebar-sub__link"
                      :target="child.external ? '_blank' : undefined"
                      :rel="child.external ? 'noopener noreferrer' : undefined"
                      @click="onChildClick"
                    >
                      {{ child.label }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>

          <div v-if="$slots.default" class="mobile-sidebar__footer">
            <slot />
          </div>
        </div>
      </aside>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import GcsaLogo from './GcsaLogo.vue';
import UiNavIcon from './UiNavIcon.vue';
import {
  resolveGcsaChrome,
  type GcsaChromeLocale,
  type GcsaMobileNavItem,
  type GcsaMobileSidebarSnapshot
} from '../consts/gcsaChromeContent';
import { CE_THEME_TOKENS } from '../composables/useCeHostTheme';

const props = withDefaults(
  defineProps<{
    open?: boolean;
    locale?: GcsaChromeLocale;
    activeNavKey?: string;
    theme?: 'dark' | 'light' | '';
    logoTitle?: string;
    logoTagline?: string;
    logoHref?: string;
    navItems?: GcsaMobileNavItem[];
    menuAriaLabel?: string;
    closeAriaLabel?: string;
  }>(),
  {
    open: false,
    locale: 'zh-CN',
    activeNavKey: '',
    theme: '',
    logoTitle: undefined,
    logoTagline: undefined,
    logoHref: undefined,
    navItems: undefined,
    menuAriaLabel: undefined,
    closeAriaLabel: undefined
  }
);

const emit = defineEmits<{ close: [] }>();

const expandedSection = ref<string | null>(null);
let previousOverflow = '';

const themeClass = computed(() => {
  if (props.theme === 'dark') return 'mobile-sidebar-theme--dark';
  if (props.theme === 'light') return 'mobile-sidebar-theme--light';
  return '';
});

/** iOS-safe inline tokens when theme is explicit (CE passes resolvedTheme). */
const themeTokenStyle = computed(() => {
  if (props.theme === 'dark') return CE_THEME_TOKENS.dark;
  if (props.theme === 'light') return CE_THEME_TOKENS.light;
  return undefined;
});

const resolved = computed<GcsaMobileSidebarSnapshot>(() => {
  const base = resolveGcsaChrome(props.locale);
  return {
    ...base,
    menuAriaLabel: props.menuAriaLabel || base.menuAriaLabel,
    closeAriaLabel: props.closeAriaLabel || base.closeAriaLabel,
    navItems: props.navItems?.length ? props.navItems : base.navItems,
    logo: {
      ...base.logo,
      title: props.logoTitle ?? base.logo.title,
      tagline: props.logoTagline ?? base.logo.tagline,
      href: props.logoHref ?? base.logo.href
    }
  };
});

const resolvedLogoTitle = computed(() => resolved.value.logo.title);
const resolvedLogoTagline = computed(() => resolved.value.logo.tagline);
const resolvedLogoHref = computed(() => resolved.value.logo.href);
/** 官网侧边栏默认不显示副标题；传入 logoTagline 时才显示（如 Finger） */
const showLogoTagline = computed(
  () => props.logoTagline != null && String(props.logoTagline).length > 0
);

const hasSubmenu = (section: GcsaMobileNavItem) =>
  Boolean(section.children && section.children.length > 0);

const emitClose = () => emit('close');

const onLogoClick = () => {
  emitClose();
};

const onChildClick = () => {
  emitClose();
};

const onTileClick = (event: MouseEvent, section: GcsaMobileNavItem) => {
  if (hasSubmenu(section)) {
    event.preventDefault();
    expandedSection.value =
      expandedSection.value === section.key ? null : section.key;
    return;
  }
  emitClose();
};

const lockBody = () => {
  if (typeof document === 'undefined') return;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
};

const unlockBody = () => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = previousOverflow;
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') emitClose();
};

watch(
  () => props.open,
  (isOpen) => {
    if (typeof window === 'undefined') return;
    if (isOpen) {
      expandedSection.value = null;
      lockBody();
      window.addEventListener('keydown', onKeydown);
    } else {
      unlockBody();
      window.removeEventListener('keydown', onKeydown);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (typeof window === 'undefined') return;
  unlockBody();
  window.removeEventListener('keydown', onKeydown);
});
</script>
